<script setup>
import { ref, computed, watch, nextTick, getCurrentInstance } from 'vue';
import { loadStripe } from '@stripe/stripe-js/pure';

const { proxy } = getCurrentInstance();
const store = proxy.$store;

// --- State ---
const activeIndex = ref(-1);
const selectedPlan = ref(undefined);
const plans = ref([]);
const card = ref(undefined);
const cardError = ref('');
const subscribing = ref(false);
const paymentMethod = ref(null);
const stripeInstance = ref(null);

// --- Computed ---
const priceId = computed(() => store.state.settings.subscriptionPriceId);
const subscriptionActive = computed(() => store.state.settings.subscriptionActive);

const paymentMethodId = computed(() => {
    return store.state.settings.subscriptionData?.paymentMethodId;
});

const plan = computed(() => {
    return subscriptionActive.value ? plans.value[0] : null;
});

const brand = computed(() => {
    if (paymentMethod.value) {
        const b = paymentMethod.value.card.brand;
        return b.charAt(0).toUpperCase() + b.slice(1);
    }
    return '';
});

// --- Initialization (Async Logic Replacement) ---

// Load Stripe Instance
watch(() => true, async () => {
    stripeInstance.value = await loadStripe('pk_test_KOopaRx8A8XR0p9QhesQ1b5R');
}, { immediate: true });

// Fetch Payment Method
watch(paymentMethodId, async (id) => {
    if (id) {
        const obj = await store.dispatch('request/get', {
            url: 'api/stripe-payment-method/' + id
        });
        paymentMethod.value = obj.data.paymentMethod;
    }
}, { immediate: true });

// Initial Plan Fetch
const setPlan = async () => {
    if (priceId.value) {
        const priceObj = await store.dispatch('request/get', { url: 'api/stripe-price/' + priceId.value });
        const price = priceObj.data.price;

        const productObj = await store.dispatch('request/get', { url: 'api/stripe-product/' + price.product });
        const product = productObj.data.product;

        plans.value.push({
            amount: Math.abs(price.unit_amount / 100),
            name: product.name
        });
    }
};
setPlan();

// --- Methods ---

const displayError = (event) => {
    cardError.value = event.error ? event.error.message : '';
};

const selectPlan = (index) => {
    activeIndex.value = index;
    selectedPlan.value = plans.value[index];

    nextTick(() => {
        if (!stripeInstance.value) return;

        const elements = stripeInstance.value.elements();
        card.value = elements.create('card', {});
        card.value.mount('#card-element');

        card.value.on('change', (event) => {
            displayError(event);
        });
    });
};

const createCustomer = async () => {
    const obj = await store.dispatch('request/post', {
        url: 'api/stripe-customer',
        postData: {
            email: store.state.user.user.email,
            name: store.state.user.user.displayName,
        }
    });
    return obj.data.customer;
};

const createSubscription = async ({ customerId, paymentMethodId, priceId }) => {
    try {
        const obj = await store.dispatch('request/post', {
            url: 'api/stripe-subscription',
            postData: { customerId, paymentMethodId, priceId }
        });

        if (obj.data.error) throw obj.data.error;

        const data = {
            paymentMethodId,
            priceId,
            subscription: obj.data
        };

        const afterAction = await handlePaymentThatRequiresCustomerAction(data);
        const afterRequires = await handleRequiresPaymentMethod(afterAction);
        await onSubscriptionComplete(afterRequires);

    } catch (error) {
        subscribing.value = false;
        cardError.value = error;
    }
};

const createPaymentMethod = async ({ customerId }) => {
    const result = await stripeInstance.value.createPaymentMethod({
        type: 'card',
        card: card.value,
        billing_details: { name: store.state.user.user.displayName },
    });

    if (result.error) {
        displayError(result);
        subscribing.value = false;
    } else {
        await createSubscription({
            customerId: customerId,
            paymentMethodId: result.paymentMethod.id,
            priceId: priceId.value
        });
    }
};

const onSubmit = async () => {
    subscribing.value = true;
    try {
        const customer = await createCustomer();
        await createPaymentMethod({ customerId: customer.id });
    } catch (e) {
        subscribing.value = false;
        cardError.value = e.message || e;
    }
};

const handlePaymentThatRequiresCustomerAction = async ({ subscription, invoice, priceId, paymentMethodId, isRetry }) => {
    if (subscription?.status === 'active') return { subscription, priceId, paymentMethodId };

    let paymentIntent = invoice ? invoice.payment_intent : subscription.latest_invoice?.payment_intent;

    if (paymentIntent && (paymentIntent.status === 'requires_action' || (isRetry && paymentIntent.status === 'requires_payment_method'))) {
        const result = await stripeInstance.value.confirmCardPayment(paymentIntent.client_secret, {
            payment_method: paymentMethodId
        });

        if (result.error) throw result;
        if (result.paymentIntent.status === 'succeeded') {
            return { priceId, subscription, invoice, paymentMethodId };
        }
    }
    return { subscription, priceId, paymentMethodId };
};

const handleRequiresPaymentMethod = async ({ subscription, paymentMethodId, priceId }) => {
    if (subscription.status === 'active') return { subscription, priceId, paymentMethodId };

    if (subscription.latest_invoice.payment_intent.status === 'requires_payment_method') {
        localStorage.setItem('latestInvoiceId', subscription.latest_invoice.id);
        localStorage.setItem('latestInvoicePaymentIntentStatus', subscription.latest_invoice.payment_intent.status);
        throw 'Your card was declined.';
    }
    return { subscription, priceId, paymentMethodId };
};

const onSubscriptionComplete = ({ paymentMethodId, priceId, subscription }) => {
    subscribing.value = false;
    let isActive = 0;

    if (['active', 'trialing'].includes(subscription.status)) {
        isActive = 1;
    }

    store.dispatch('itemData/update', {
        tableName: 'account_settings',
        id: 1,
        data: {
            subscriptionActive: isActive,
            subscriptionData: {
                subscriptionStatus: subscription.status,
                paymentMethodId,
                subscriptionId: subscription.id,
                customerId: subscription.customer,
                priceId,
                productId: subscription.items.data[0].price.product,
                currentPeriodEnd: subscription.current_period_end,
            }
        },
    });
};
</script>

<template>
    <div class="container-fluid">
        <template v-if="!subscriptionActive">
            <h1>Subscribe to a plan</h1>

            <div class="text-center plans">
                <div v-if="!plans.length" class="ajax-spinner">
                    <span class="ajax-spinner-icon mt-3"/>
                </div>

                <div
                    v-for="(plan, index) in plans"
                    v-else
                    :key="index"
                    :class="{'active': index === activeIndex}"
                    class="card plan"
                >
                    <div class="card-body text-left">
                        <h2 class="plan-heading">{{ plan.name }}</h2>
                        <p class="price">£{{ plan.amount }}</p>
                        <p class="price-label">
                            Per month<br>Billed monthly
                        </p>
                        <div class="text-center">
                            <button class="btn btn-light" @click="selectPlan(index)">
                                {{ index === activeIndex ? 'Selected' : 'Select' }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="selectedPlan" class="card subscription-panel">
                <div class="card-body">
                    <h2 class="subscription-card-header">
                        Enter your card details.<br>
                        Your subscription will start <span class="highlight">XXX</span>.
                    </h2>
                    <p class="subscription-panel-info">
                        → Total due now <strong>£{{ selectedPlan.amount }}</strong><br>
                        → Subscribing to <strong>{{ selectedPlan.name }}</strong>
                    </p>

                    <div>
                        <form id="payment-form" :disabled="subscribing" @submit.prevent="onSubmit">
                            <label>Card</label>
                            <div id="card-element"></div>

                            <div v-if="cardError" id="card-element-errors" class="alert alert-danger" role="alert">
                                {{ cardError }}
                            </div>

                            <p class="text-center">
                                <button :class="{'active': subscribing}" :disabled="subscribing" class="btn btn-warning" type="submit">
                                    {{ subscribing ? 'Subscribing' : 'Subscribe' }}
                                </button>
                            </p>
                        </form>
                    </div>
                </div>
            </div>
        </template>

        <template v-if="subscriptionActive && plan && paymentMethod">
            <h1>Current plan</h1>
            <div class="card subscribed-panel">
                <div class="card-body">
                    <h2>{{ plan.name }}</h2>
                    <p class="subscription-panel-info">
                        Amount <strong class="float-end">£{{ plan.amount }}</strong><br>
                        Credit card <strong class="float-end">{{ brand }} •••• {{ paymentMethod.card.last4 }}</strong>
                    </p>
                </div>
            </div>
        </template>
    </div>
</template>

<style scoped>
/* Scoped styles preserved exactly as provided */
h1 { margin: 50px 0 30px; text-align: center; color: tomato; font-size: 28px; }
.plans { margin-bottom: 30px; }
.plan { display: inline-block; margin: 0 15px; width: 200px; }
.plan-heading { margin-top: 0; color: #aaa; font-weight: 400; font-size: 18px; }
.price { margin-bottom: 0; font-size: 26px; font-weight: bold; color: tomato; }
.price-label { margin-bottom: 15px; font-size: 12px; color: #999; }
.plan.active button { background-color: white; color: tomato; }
.subscription-panel { margin: 0 auto; width: 500px; }
.subscription-card-header { margin-top: 0; font-size: 21px; line-height: 1.4; }
.subscription-panel-info { margin-bottom: 15px; font-size: 18px; color: #666; }
.subscription-panel-info strong { color: #333; }
label { text-transform: uppercase; font-weight: 700; color: #666; font-size: 13px; }
#card-element { margin: 0 0 15px; padding: 15px; background: whitesmoke; border: 1px solid lavender; border-radius: 5px; }
.subscribed-panel { margin: 0 auto; width: 500px; }
.highlight { color: red !important; }
</style>